import React, { useMemo } from 'react';
import { Command } from 'cmdk';
import { Bolt, Coins, PencilRuler, RotateCcw, UserPlus, Users, UsersRound } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import { cn } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

const ACTION_DEFS = [
  { id: 'quick-test', labelKey: 'quick.test', hintKey: 'quick.testHint', icon: Bolt },
  {
    id: 'plan-templates',
    labelKey: 'quick.plans',
    path: '/gestor-xui/categories',
    hintKey: 'quick.plansHint',
    icon: PencilRuler,
  },
  {
    id: 'clients',
    labelKey: 'quick.clients',
    path: '/gestor-xui/clients',
    hintKey: 'quick.clientsHint',
    icon: Users,
  },
  {
    id: 'add-client',
    labelKey: 'quick.addClient',
    path: '/gestor-xui/clients',
    hintKey: 'quick.addClientHint',
    icon: UserPlus,
  },
  {
    id: 'renew-client',
    labelKey: 'quick.renew',
    path: '/gestor-xui/clients',
    hintKey: 'quick.renewHint',
    icon: RotateCcw,
  },
  {
    id: 'resellers',
    labelKey: 'quick.resellers',
    path: '/gestor-xui/resellers',
    hintKey: 'quick.resellersHint',
    icon: UsersRound,
  },
  {
    id: 'add-credits',
    labelKey: 'quick.credits',
    path: '/buy-credits',
    hintKey: 'quick.creditsHint',
    icon: Coins,
  },
];

const paletteInputWrap =
  'mx-3 mt-3 rounded-lg border border-border bg-secondary/60 px-2 shadow-sm';

const paletteFooter =
  'border-t border-border/80 px-4 pt-3 text-[10px] text-muted-foreground [&_kbd]:rounded [&_kbd]:border [&_kbd]:border-border [&_kbd]:bg-muted/60 [&_kbd]:px-1.5 [&_kbd]:py-0.5 [&_kbd]:font-mono [&_kbd]:text-muted-foreground';

const itemSelected =
  'aria-selected:bg-foreground/[0.06] aria-selected:text-foreground aria-selected:ring-1 aria-selected:ring-border';

export function DesktopQuickActionsModal({
  open,
  onOpenChange,
  modules,
  onOpenQuickTest,
  onNavigate,
}) {
  const { t } = useLanguage();

  const allowedPaths = useMemo(() => {
    const set = new Set();
    (modules || []).forEach((m) => (m.items || []).forEach((it) => set.add(it.path)));
    return set;
  }, [modules]);

  const visibleActions = useMemo(
    () =>
      ACTION_DEFS.filter((a) => !a.path || allowedPaths.has(a.path)).map((a) => ({
        ...a,
        label: t(a.labelKey),
        hint: t(a.hintKey),
      })),
    [allowedPaths, t],
  );

  const handleSelect = (action) => {
    if (action.id === 'quick-test') {
      onOpenQuickTest?.();
      onOpenChange(false);
      return;
    }
    if (action.path) {
      onNavigate?.(action.path);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-2xl gap-0 overflow-hidden p-0 pb-4 sm:rounded-2xl')}
        aria-describedby={undefined}
      >
        <OfficeModalHeader
          layout="stack"
          variant="circle"
          icon={Bolt}
          title={t('quick.title')}
          className="office-modal-header px-4 sm:px-6"
        />
        <Command className="rounded-none bg-transparent text-foreground">
          <div className={paletteInputWrap}>
            <Command.Input
              placeholder={t('quick.search')}
              className={cn(
                'flex h-10 w-full border-0 bg-transparent px-2 text-sm text-foreground outline-none',
                'placeholder:text-muted-foreground',
              )}
            />
          </div>
          <Command.List className="max-h-[min(62vh,520px)] overflow-y-auto px-2 py-2">
            <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
              {t('quick.empty')}
            </Command.Empty>
            <Command.Group
              heading={t('quick.recent')}
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              {visibleActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Command.Item
                    key={action.id}
                    value={`${action.label} ${action.hint || ''} ${action.path || ''}`}
                    onSelect={() => handleSelect(action)}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground',
                      '[&[aria-selected=true]_svg]:text-primary [&[data-selected=true]_svg]:text-primary',
                      itemSelected,
                    )}
                  >
                    <Icon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold">{action.label}</p>
                      <p className="truncate text-[11px] text-muted-foreground">{action.hint || action.path}</p>
                    </div>
                  </Command.Item>
                );
              })}
            </Command.Group>
          </Command.List>
          <div className={paletteFooter}>
            <kbd>Esc</kbd>
            {' '}{t('quick.footerClose')} ·{' '}
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            {' '}{t('quick.footerNav')} ·{' '}
            <kbd>↵</kbd>
            {' '}{t('quick.footerOpen')} ·{' '}
            <kbd>Ctrl</kbd>
            <kbd>/</kbd>
            {' '}{t('quick.footerShortcut')}
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
